// The name this NUMBER presents on outbound calls.
//
// Per-number, and it always was — `dids.commio_caller_id_id` (migration 0038) is the
// override and `subscribers.commio_caller_id_id` is the default it falls back to. But the
// only editor lived nested inside the DID page's *Subscriber* card, under a comment
// saying caller ID belongs with the customer, so it read as an account-level setting that
// happened to be reachable from a number. Kevin read it that way, which means it was that
// — a control's meaning is where it sits, not what the schema underneath it allows.
//
// So it is its own card, on the number and on the line, and it says which of the two
// states it is in. The distinction matters in one direction: editing an inherited number
// creates a NEW carrier profile for that number rather than renaming the customer's, so
// the customer's other numbers keep the name they had. Editing a number that already has
// its own profile renames that one. Both are what an operator means; neither is obvious
// from a text field, hence the sentence above the field rather than a tooltip.
//
// Fifteen characters because that is the field CNAM is delivered in. Longer names are
// truncated by carriers downstream, so the limit is enforced here and again at the API.
//
// TWO SHAPES, because Commio has two — and those rules now live in `lib/cnam.js`, because
// the line's settings sheet edits the same field behind the single gear on its Overview card
// (Kevin, 2026-08-19). A residential record needs a first AND a last name, a business one a
// single business name, 15 characters either way; which shape applies comes from the number's
// own type, since the carrier requires the caller-ID type to match the 911 site type. Two
// copies of that would drift, and the drift would show up as a carrier refusal.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import GearButton from '../shared/GearButton.jsx';
import { CNAM_MAX, cnamShapeHint, cnamState, splitCnam } from '../../lib/cnam.js';

export default function CallerIdCard({
  e164,
  cnam,
  // 'business' | 'residential' | null. Decides which fields Commio needs. Null means we do
  // not know it here, and the form then asks for both shapes' worth rather than guessing.
  cnamType,
  callerIdId,
  // The CUSTOMER's default profile. "Follows the customer" is only true if there is
  // something to follow: line 132 showed "Follows Kevin Mahoney's name" while the pending
  // check correctly said the number had no carrier profile at all, because neither the
  // number nor the customer had one (2026-08-04). Inheritance from nothing is not
  // inheritance. `undefined` means the caller did not supply it — then this stays quiet
  // rather than guessing.
  subscriberCallerIdId,
  subscriberId,
  subscriberName,
  canEdit,
  onSaved,
  // 'card' (its own bordered card, as on the DID page) or 'field' (a labelled cell inside
  // somebody else's card). Kevin, 2026-08-13: caller ID and the 911 address belong in the
  // line's Overview card beside status and package, not in two cards of their own below it.
  // The EDITOR is identical either way — the sheet is what owns the two-shape carrier rule,
  // and a second copy of it for the compact rendering is how those two would drift.
  variant = 'card',
}) {
  // WHAT THE CARRIER ACTUALLY HOLDS, which is not what `cnamType` says.
  //
  // `cnamType` is DERIVED — the caller passes the line's address type (DidDetail.jsx). It is what
  // the type SHOULD be, and the card has always presented it as though it were the carrier's. On
  // Lee Hall's +13038235515 the page said business, the whole form behaved as business, and Commio
  // held a RESIDENTIAL profile — so a build refused the 911 site with `412 Caller ID type and
  // Location type must match` while every word on screen agreed with itself (Kevin, 2026-09-03:
  // "verso says the caller id is business").
  //
  // This card's own history is the reason to be careful here: it used to print an amber "no carrier
  // profile" note inferred from an empty column of ours, on accounts that were working. So this one
  // is MEASURED — it says nothing at all unless the carrier answered and the two genuinely differ.
  // org_admin only, because it spends a carrier credential; org_ro simply does not ask.
  const carrierProfile = useQuery({
    queryKey: ['commio-caller-id', callerIdId],
    queryFn: () => api.get(`/settings/commio/caller-id/${callerIdId}`),
    enabled: Boolean(canEdit && callerIdId != null),
    staleTime: 60_000,
    retry: false,
  });
  const carrierType = carrierProfile.data?.caller_id_type || null;
  const typeDrift = Boolean(cnamType && carrierType
    && String(carrierType).toLowerCase() !== String(cnamType).toLowerCase());

  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState(cnam || '');
  // Residential: the two parts, seeded by splitting the recorded name on the last space —
  // the same shape the carrier holds. The operator can correct it, which is the point.
  const seed = splitCnam(cnam);
  const [first, setFirst] = useState(seed.first);
  const [last, setLast] = useState(seed.last);

  // The number's own profile, or the customer's. NULL means "follows the customer" —
  // never "no name", which is a different state and rendered differently below.
  const own = callerIdId != null;
  // `subscriberCallerIdId` is no longer read: the card shows the name and nothing else
  // (see the render). It stays in the props because callers pass it and the editor's
  // create-vs-rename behaviour still depends on `own`.
  void subscriberCallerIdId;

  useEffect(() => {
    setTyped(cnam || '');
    const next = splitCnam(cnam);
    setFirst(next.first);
    setLast(next.last);
  }, [cnam]);

  const save = useMutation({
    // Carrier first, Verso second: the number presenting the right name matters more than
    // our record of it, and a failed carrier write must not leave Verso claiming success.
    mutationFn: (payload) => api.put(`/dids/${encodeURIComponent(e164)}/cnam`, payload),
    onSuccess: (updated) => { setEditing(false); onSaved?.(updated); },
  });

  // What Save will send, and whether it is complete enough for the carrier to accept.
  const { residential, payload, combined, complete, tooLong } = cnamState(cnamType, { typed, first, last });
  const unchanged = combined === (cnam || '');

  const field = variant === 'field';

  if (!subscriberId) {
    // Nothing to inherit from and no carrier profile to make: the caller-ID type has to
    // match the customer's 911 site type, so this needs a customer first.
    const empty = (
      <p className="text-sm text-gray-500">
        Assign this number to a customer before setting the name it presents.
      </p>
    );
    return field ? <Field label="Caller ID">{empty}</Field> : <Card>{empty}</Card>;
  }

  // A gear and a sheet, like every other setting on this page (Kevin, 2026-08-11). The
  // current name stays visible behind it, so the operator can see what they are changing
  // from while they type.
  const gear = canEdit && (
    <GearButton
      label={cnam ? 'Change the caller ID' : 'Set the caller ID'}
      onClick={() => { save.reset(); setTyped(cnam || ''); setEditing(true); }}
    />
  );

  const Shell = field ? Field : Card;

  return (
    <Shell label="Caller ID" action={gear}>
      {(
        <div className={field ? 'mt-1' : 'mt-3'}>
          {/* JUST THE NAME.
              This carried three explanatory states — an amber "no carrier profile" note,
              "Set for this number only — the customer's other numbers are unaffected", and
              "Follows X's name. Changing it here affects only this number."

              Kevin, 2026-08-17: "this message is not really helpful either." He is right on
              both counts. The follows/override distinction is Verso's own bookkeeping — the
              operator asked what name this number shows, and the gear already changes it for
              this number only whichever way it is stored. And the amber note asserted the
              carrier had no profile, which Verso cannot know: there is no Commio caller-ID
              read anywhere, so it was an inference from an empty column of ours, printed as
              a warning on accounts that were working (feedback_probe_dont_trust_our_row).

              What is left is the name, or the absence of one. Both are true, and neither
              needs a paragraph. */}
          {cnam ? (
            <div className="text-sm font-medium text-gray-900">{cnam}</div>
          ) : (
            <p className="text-sm text-gray-500">
              No name set — this number has no caller ID recorded in Verso.
            </p>
          )}
          {/* MEASURED, not inferred: shown only when the carrier answered and disagrees. Named
              because the consequence is a build that stops at step 0 with a carrier error code
              and nothing on this page to explain it. */}
          {typeDrift && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
              The carrier holds this as a <span className="font-medium">{carrierType}</span> caller-ID
              profile (#{callerIdId}), but this number’s address is{' '}
              <span className="font-medium">{cnamType}</span>. Verso’s answer is the {cnamType} one, and
              a build will now push it — the 911 step gives this number a {cnamType} profile and sets
              the site in the same carrier order. Press the gear to do it now instead.
            </p>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Caller ID for {e164}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                The name people see when this number calls them. This one reaches the carrier
                when you save — it is not held for the next build.
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
          <p className="text-xs text-gray-500">
            {own
              ? 'This number has its own name at the carrier. Saving renames it.'
              : `This number currently follows ${subscriberName || 'the customer'}. Saving gives it a name of `
                + 'its own, leaving their other numbers as they are.'}
          </p>
          {residential ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="cnam-first" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  First name
                </label>
                <input
                  id="cnam-first"
                  autoFocus
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="cnam-last" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Last name
                </label>
                <input
                  id="cnam-last"
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="cnam-name" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                {cnamType === 'business' ? 'Business name' : 'Name to present'}
              </label>
              <input
                id="cnam-name"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          )}
          <p className="text-xs text-gray-500">
            {cnamShapeHint(cnamType)}
            {' '}
            <span className={tooLong ? 'font-medium text-red-700' : undefined}>
              {CNAM_MAX - combined.length} of {CNAM_MAX} characters left
            </span>
            {' — carriers cut off the rest.'}
          </p>

          {save.isError && (
            <p className="text-sm text-red-700">{save.error?.body?.error || save.error?.message}</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={save.isPending || !complete || unchanged || tooLong}
              onClick={() => save.mutate(payload)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? 'Submitting to carrier…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); save.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {own && (
              // The way back. Without it an override is permanent, and the operator who
              // set a name for one number has no way to put it back on the customer's.
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate({ inherit: true })}
                title={`Drop this number's own name and follow ${subscriberName || 'the customer'} again`}
                className="ml-auto rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Follow the customer’s name
              </button>
            )}
          </div>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Card({ children, action }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Caller ID</h2>
          <p className="mt-1 text-sm text-gray-600">
            The name people see when this number calls them.
          </p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// The same thing as a cell in somebody else's card: the label in the small-caps style the
// rest of that card's fields use, and the gear on the label row rather than the card's.
function Field({ label, action, children }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
        {action}
      </div>
      <dd>{children}</dd>
    </div>
  );
}
